import { getTokenExpiryMs } from './jwt-utils';
import { refreshAccessToken } from './refresh-token';
import type { RefreshResult } from './refresh-token';
import { emitAuthExpired } from './auth-events';

/** How early we refresh before the actual JWT expiry. Picks a window large
 *  enough to absorb network latency + clock skew. */
const REFRESH_LEAD_MS = 60_000;

/** Minimum delay before firing — never schedule a setTimeout for 0 or
 *  negative values. The kicker covers tokens that are already expired (we
 *  let the reactive 401 path handle those). */
const MIN_DELAY_MS = 1_000;

/** Backoff schedule for transient refresh failures (network drop, 5xx).
 *  Total wall-clock retry budget: ~40 s before we give up and let the user
 *  fall through to the reactive 401 path. */
const RETRY_BACKOFFS_MS = [0, 2_000, 8_000, 30_000];

let refreshTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Run `refreshAccessToken()` with exponential backoff. Stops as soon as
 * the call succeeds OR returns a terminal failure (401/403 — refresh
 * token is actually invalid). Transient failures (network, 5xx) get
 * retried; without retries any 1-second wifi hiccup at the proactive
 * window would log the user out.
 */
async function refreshWithRetries(): Promise<RefreshResult> {
  let last: RefreshResult = { success: false, terminal: false };
  for (const delay of RETRY_BACKOFFS_MS) {
    if (delay > 0) await new Promise((r) => setTimeout(r, delay));
    last = await refreshAccessToken();
    if (last.success) return last;
    if (last.terminal) return last; // refresh token actually rejected — stop
  }
  return last;
}

/**
 * Arms a one-shot timer that calls `refreshAccessToken()` ~60s before the
 * supplied JWT's `exp` claim. On success, re-arms with the new token; on
 * terminal failure, fires `emitAuthExpired()` so AuthContext can clear
 * in-memory state and redirect to sign-in. Transient failures are
 * silently retried with backoff and do NOT log the user out.
 */
export function scheduleProactiveRefresh(token: string | null | undefined): void {
  cancelProactiveRefresh();
  if (!token) return;

  const expiryMs = getTokenExpiryMs(token);
  if (expiryMs === null) return; // unknown TTL → fall back to reactive 401

  const now = Date.now();
  const delay = Math.max(MIN_DELAY_MS, expiryMs - now - REFRESH_LEAD_MS);

  refreshTimer = setTimeout(async () => {
    refreshTimer = null;
    try {
      const res = await refreshWithRetries();
      if (res.success && res.accessToken) {
        scheduleProactiveRefresh(res.accessToken);
      } else if (res.terminal) {
        await emitAuthExpired();
      }
      // Transient final failure: don't expire — the reactive 401 path
      // will pick it up on the user's next request.
    } catch {
      // Defensive: any unexpected throw is treated as transient. The
      // reactive 401 path catches it later.
    }
  }, delay);
  // Don't keep node/jest event loops alive solely for this timer.
  (refreshTimer as { unref?: (() => void) | undefined}).unref?.();
}

/** Cancels any armed refresh. Idempotent. */
export function cancelProactiveRefresh(): void {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

/** For tests. Returns true if a timer is currently armed. */
export function _hasArmedTimer(): boolean {
  return refreshTimer !== null;
}
